import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { EMPTY, Subscription, fromEvent, interval, merge } from 'rxjs';
import { catchError, exhaustMap, filter } from 'rxjs/operators';
import { NotificationService } from './notification.service';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MAIN_SCRIPT = /\bsrc="(main-[A-Z0-9]{8}\.js)"/;

// The content-hashed main bundle name is the release identity, so no version file is needed.
// ponytail: a translation-only release does not change it, but i18n JSON is served no-cache anyway.
export function extractMainScript(html: string): string | null {
  return MAIN_SCRIPT.exec(html)?.[1] ?? null;
}

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  // Straight to the backend, skipping interceptors: no token on the poll, no error toast when it fails.
  private readonly _http = new HttpClient(inject(HttpBackend));
  private readonly _document = inject(DOCUMENT);
  private readonly _notificationService = inject(NotificationService);
  private readonly _transloco = inject(TranslocoService);

  start(): Subscription {
    const running = this._document
      .querySelector('script[src^="main-"]')
      ?.getAttribute('src');
    if (!running) {
      return Subscription.EMPTY;
    }

    const tabShown$ = fromEvent(this._document, 'visibilitychange').pipe(
      filter(() => this._document.visibilityState === 'visible'),
    );

    return merge(interval(POLL_INTERVAL_MS), tabShown$)
      .pipe(
        exhaustMap(() =>
          this._http
            .get('/index.html', { responseType: 'text' })
            .pipe(catchError(() => EMPTY)),
        ),
        filter((html) => {
          const deployed = extractMainScript(html);
          return deployed !== null && deployed !== running;
        }),
      )
      .subscribe(() => this.promptReload());
  }

  // No once-only latch: any later toast replaces this one, so every detection re-shows it.
  promptReload(): void {
    this._notificationService.show(
      this._transloco.translate('appUpdate.available'),
      'info',
      {
        durationMs: null,
        action: {
          label: this._transloco.translate('appUpdate.reload'),
          run: () => this._document.defaultView?.location.reload(),
        },
      },
    );
  }
}
